import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ChevronDown, ChevronRight, Loader2, Check, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { NotesModal, fmtDate, fmtTime } from '../approvals/shared';

const API = `${window.__BACKEND_URL__}/api`;

const ApproveModal = ({ token, entry, onDone, onClose }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ pay_code: entry.pay_code || '', notes: '' });
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/time/${entry.id}/payroll-approve`, {
        pay_code: form.pay_code || null,
        notes: form.notes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Payroll approved' });
      onDone();
      onClose();
    } catch (e) {
      toast({ title: 'Could not approve', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="payroll-approve-modal">
        <DialogHeader>
          <DialogTitle>Payroll approve — {fmtDate(entry.clock_in)}</DialogTitle>
          <DialogDescription>Optionally set a pay code before approving.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Pay code (optional)</Label>
            <Input value={form.pay_code} onChange={(e) => setForm({ ...form, pay_code: e.target.value })}
              placeholder="e.g. REG, STAT, OT1.5" data-testid="payroll-approve-paycode" />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="payroll-approve-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="payroll-approve-cancel">Cancel</Button>
          <Button onClick={approve} disabled={busy} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="payroll-approve-confirm">
            {busy ? 'Approving…' : '✅ Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PayrollQueue = ({ token, period, onChanged, refreshKey }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [workers, setWorkers] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [approving, setApproving] = useState(null);
  const [sendingBack, setSendingBack] = useState(null);
  const [flagging, setFlagging] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (period.start) params.start_date = period.start;
      if (period.end) params.end_date = period.end;
      const { data } = await axios.get(`${API}/time/payroll-queue`, { ...auth, params });
      setWorkers(data.workers || []);
    } catch (e) { setWorkers([]); }
  }, [token, period.start, period.end]); // eslint-disable-line

  useEffect(() => { load(); }, [load, refreshKey]);

  const refreshAll = () => { load(); onChanged?.(); };

  const sendBack = async (reason) => {
    try {
      await axios.put(`${API}/time/${sendingBack.id}/payroll-send-back`, { reason }, auth);
      toast({ title: 'Sent back to supervisor queue' });
      setSendingBack(null);
      refreshAll();
    } catch (e) {
      toast({ title: 'Could not send back', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const flagException = async (reason) => {
    try {
      await axios.put(`${API}/time/${flagging.id}/payroll-flag-exception`, { reason }, auth);
      toast({ title: 'Flagged as payroll exception' });
      setFlagging(null);
      refreshAll();
    } catch (e) {
      toast({ title: 'Could not flag', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const approveAllFor = async (w) => {
    setBulkBusy(w.user_id);
    try {
      const { data } = await axios.post(`${API}/time/payroll-bulk-approve`,
        { entry_ids: w.entries.map((e) => e.id) }, auth);
      toast({ title: `Approved for ${w.user_name}`, description: `${data.approved ?? data.processed ?? w.entries.length} entries approved${data.failed ? `, ${data.failed} failed` : ''}` });
      refreshAll();
    } catch (e) {
      toast({ title: 'Bulk approve failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBulkBusy(null); }
  };

  if (workers === null) {
    return <div className="py-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>;
  }

  if (workers.length === 0) {
    return (
      <div className="ht-card p-12 text-center">
        <p className="text-sm" style={{ color: '#64748B' }} data-testid="payroll-queue-empty">
          No supervisor-approved timesheets waiting for payroll review in this period.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="payroll-queue">
      {workers.map((w) => {
        const isCollapsed = collapsed[w.user_id];
        return (
          <div key={w.user_id} className="ht-card p-0 overflow-hidden" data-testid={`worker-group-${w.user_id}`}>
            <div className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none"
              style={{ borderBottom: isCollapsed ? 'none' : '1px solid #2D3748' }}
              onClick={() => setCollapsed((c) => ({ ...c, [w.user_id]: !c[w.user_id] }))}
              data-testid={`worker-header-${w.user_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                {isCollapsed ? <ChevronRight className="w-4 h-4" style={{ color: '#64748B' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#64748B' }} />}
                <span className="text-sm font-semibold text-white truncate">{w.user_name || 'Unknown worker'}</span>
                <span className="text-xs whitespace-nowrap" style={{ color: '#A0AEC0' }}>
                  Reg <span className="text-white font-semibold">{w.regular_hours.toFixed(2)}</span>
                  {' · '}OT <span className="font-semibold" style={{ color: w.overtime_hours > 0 ? '#FFA000' : '#A0AEC0' }}>{w.overtime_hours.toFixed(2)}</span>
                  {' · '}Total <span className="text-white font-semibold">{w.total_hours.toFixed(2)}h</span>
                </span>
              </div>
              <Button size="sm" onClick={(ev) => { ev.stopPropagation(); approveAllFor(w); }}
                disabled={bulkBusy === w.user_id}
                className="bg-[#00C896] hover:bg-emerald-600 text-white font-semibold shrink-0"
                data-testid={`approve-all-worker-${w.user_id}`}>
                {bulkBusy === w.user_id ? 'Approving…' : `✅ Approve All for ${w.user_name || 'worker'}`}
              </Button>
            </div>
            {!isCollapsed && (
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid #1F2D3F' }}>
                    {['Date', 'Clock In', 'Clock Out', 'Reg', 'OT', 'Pay Code', 'Supervisor', 'Actions'].map((h) => (
                      <th key={h} className="text-left py-2 px-4 font-medium text-xs" style={{ color: '#64748B' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {w.entries.map((e) => (
                    <tr key={e.id} style={{ borderBottom: '1px solid #1F2D3F' }} data-testid={`payroll-row-${e.id}`}>
                      <td className="py-2 px-4 text-white whitespace-nowrap">{fmtDate(e.clock_in)}</td>
                      <td className="py-2 px-4 whitespace-nowrap" style={{ color: '#A0AEC0' }}>{fmtTime(e.clock_in)}</td>
                      <td className="py-2 px-4 whitespace-nowrap" style={{ color: '#A0AEC0' }}>{e.clock_out ? fmtTime(e.clock_out) : '—'}</td>
                      <td className="py-2 px-4 text-white">{(e.regular_hours ?? 0).toFixed(2)}</td>
                      <td className="py-2 px-4 font-semibold" style={{ color: (e.overtime_hours || 0) > 0 ? '#FFA000' : '#A0AEC0' }}>{(e.overtime_hours ?? 0).toFixed(2)}</td>
                      <td className="py-2 px-4 text-xs font-mono" style={{ color: '#A0AEC0' }}>{e.pay_code || '—'}</td>
                      <td className="py-2 px-4 text-xs" style={{ color: '#A0AEC0' }}>{e.supervisor_approved_by_name || '—'}</td>
                      <td className="py-2 px-4">
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          <button onClick={() => setApproving(e)} title="Payroll approve"
                            className="p-1.5 rounded-lg" style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}
                            data-testid={`pr-approve-btn-${e.id}`}>
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setSendingBack(e)} title="Send back to supervisor"
                            className="p-1.5 rounded-lg text-xs font-bold" style={{ background: 'rgba(255,120,80,0.15)', color: '#FB923C' }}
                            data-testid={`pr-sendback-btn-${e.id}`}>
                            ↩
                          </button>
                          <button onClick={() => setFlagging(e)} title="Flag payroll exception"
                            className="p-1.5 rounded-lg text-xs font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#F87171' }}
                            data-testid={`pr-flag-btn-${e.id}`}>
                            ⚠
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {approving && (
        <ApproveModal token={token} entry={approving} onDone={refreshAll} onClose={() => setApproving(null)} />
      )}
      {sendingBack && (
        <NotesModal title="Send back to supervisor"
          description="This clears the supervisor approval — tell them what to recheck."
          placeholder="e.g. Hours don't match the job schedule — please re-verify"
          submitLabel="Send Back" destructive
          onSubmit={sendBack} onClose={() => setSendingBack(null)} testId="sendback-modal" />
      )}
      {flagging && (
        <NotesModal title="Flag payroll exception"
          description="Marks this entry as a payroll exception until it's resolved."
          placeholder="e.g. Pay rate mismatch with contract — HR reviewing"
          submitLabel="Flag Exception" destructive
          onSubmit={flagException} onClose={() => setFlagging(null)} testId="flag-modal" />
      )}
    </div>
  );
};

export default PayrollQueue;
